'use client';

import Image from 'next/image'
import { useEffect } from 'react'
import { notifyJake } from '@/app/_requests/notifyCalls'
import { useRouter } from "next/navigation";
import { logClientError } from '@/utils/logClientError'
import { RefreshCcw, Search } from 'lucide-react';

// True when the failure looks like a stale/missing JS or CSS chunk on the CDN —
// typically a driver whose page was cached before the last deploy, whose chunk
// URLs are no longer served. This root boundary catches chunk errors for `/`
// (the payticketsnyc.com landing) and for any route that doesn't have its own
// error.jsx. Duplicated across each route's error.jsx rather than pulled into a
// shared util so each boundary stays fully self-contained.
function isChunkLoadError(err) {
    if (!err) return false
    if (err.name === 'ChunkLoadError') return true
    const msg = String(err.message || err || '')
    return /Loading chunk \d+ failed/i.test(msg)
        || /Loading CSS chunk \d+ failed/i.test(msg)
        || /ChunkLoadError/i.test(msg)
}

// Self-contained recovery UI for chunk-load failures. Inline styles only, no
// next/image, no lucide-react, no Tailwind classes, no brand fonts — must
// render even when the CSS chunk or a lucide chunk is what failed.
function ChunkRecovery() {
    const handleReload = () => {
        if (typeof window !== 'undefined') window.location.reload()
    }

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                textAlign: 'center',
                backgroundColor: '#f3f4f6',
                fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            }}
        >
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>
                This page couldn&apos;t load properly.
            </h1>
            <p style={{ margin: '12px 0 20px 0', fontSize: 16, color: '#4b5563', maxWidth: 380 }}>
                Please refresh to search your tickets.
            </p>
            <button
                type="button"
                onClick={handleReload}
                style={{
                    padding: '12px 24px',
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#ffffff',
                    backgroundColor: '#1f2937',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                }}
            >
                Refresh page
            </button>
        </div>
    )
}

// Existing non-chunk error UI extracted into its own component so its hooks
// (useRouter, useEffect + notifyJake/logClientError) only run on the non-chunk
// path — a conditional early return in the parent would violate the Rules of
// Hooks. Chunk-load errors are deploy-timing artifacts, not real production
// failures, so we skip notifyJake for them too (avoid alert flood on deploys).
function NonChunkError({ error }) {

    const router = useRouter();

    // Notify on mount (and whenever the error itself changes).
    // Wrapped in try/catch so a notify failure never blocks rendering the error page.
    useEffect(() => {

        try {

            const url = typeof window !== 'undefined' ? window.location.href : 'unknown'
            const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'

            // First few frames of the stack only — keep the message readable
            const stackPreview = error?.stack
                ? error.stack.split('\n').slice(0, 4).map(s => s.trim()).join(' | ')
                : 'no stack'

            const msg = [
                `🚨 error.jsx shown`,
                `URL: ${url}`,
                `Name: ${error?.name || 'unknown'}`,
                `Message: ${error?.message || String(error)}`,
                error?.digest ? `Digest: ${error.digest}` : null,
                `Stack: ${stackPreview}`,
                `UA: ${userAgent}`,
            ].filter(Boolean).join('\n')

            notifyJake({ msg })
            logClientError(error)

        } catch {
            // Swallow — never let an alert/logging failure break the error page.
        }

    }, [error])

    return (
        <div className="w-full h-screen flex items-center justify-center bg-gradient-to-br from-white to-[#d4d4d4] flex-col">

            <div className='w-[80px] h-[100px] '>

                <Image
                    src='/assets/images/icon2.png'
                    alt=""
                    width={100}
                    height={100}
                    className='rounded-[12.5px] shadow-lg shadow-gray-500'
                />

                <div
                    className="w-[27.5px] h-[27.5px] bg-red-600 rounded-full relative bottom-[94px] left-[66px] flex items-center justify-center"
                >
                    
                    <h1 className="font-bogueBold text-white text-[20px] relative top-[2px]">
                        !
                    </h1>

                </div>
                
            </div>
            
            <h1 className="font-tpThin text-gray-800 text-[20px] pt-[30px] pb-[15px] relative left-3">
                Something went wrong ... 
            </h1>
            

            <button 
                className="mb-[20px] px-[17.5px] py-[7.5px] bg-white rounded-full shadow-gray-500 shadow-md active:shadow-none active:opacity-50 flex flex-row items-center justify-center space-x-[5px]"
                onClick={() => 
                    router.refresh()
                }>
                    <h2 className='font-tpBold text-[18px] text-gray-800'>
                        Try again
                    </h2>

                    <RefreshCcw
                        size={16}
                        strokeWidth={3.5}
                        color='#1f2937'
                    />

            </button>

            <button
                className="px-[17.5px] py-[7.5px] bg-white rounded-full shadow-gray-500 shadow-md active:shadow-none active:opacity-50 flex flex-row items-center justify-center space-x-[5px]"
                onClick={() =>
                    router.push('/welcome')
                }>
                    <h2 className='font-tpBold text-[18px] text-gray-800'>
                        New search
                    </h2>

                    <Search
                        size={16}
                        strokeWidth={3.5}
                        color='#1f2937'
                    />

            </button>

        </div>
    );
}

// Top-level chooser — Next.js renders this on any uncaught error under the root
// segment. Chunk-load errors get the self-contained recovery UI; everything
// else keeps the existing notify+log+UI path in NonChunkError.
export default function Error({ error }) {
    if (isChunkLoadError(error)) {
        return <ChunkRecovery />
    }
    return <NonChunkError error={error} />
}